import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { listingApiIsomorphicFetchOrg } from '#/modules/listing/orgs/api/listingApiFetchOrg';
import { UnpublishedOrgPageLoadable } from '#/modules/listing/orgs/pages/unpublished/UnpublishedOrgPageLoadable';
import type { viewOrgReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonAppRouteShouldRevalidateFunctionArgs } from '#/routing/types/HorizonAppRouteShouldRevalidateFunctionArgs';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';

import { ViewOrgPageLoadable } from './ViewOrgPageLoadable';

export const handle = {
  defaultSearchType: 'ORG',
  showSearchRefinements: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
}: HorizonAppRouteShouldRevalidateFunctionArgs<typeof viewOrgReferenceRoute>) {
  return currentUrl.pathname !== nextUrl.pathname;
}

export async function loader({
  request,
  context,
  params: { orgIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewOrgReferenceRoute>) {
  // It's possible this loadable won't be used if the job is unpublished
  // but in most cases optimistically loading it is best
  await ViewOrgPageLoadable.load();

  const [orgId] = orgIdDashSlug.split('-');

  const org = await listingApiIsomorphicFetchOrg({ request, context })(orgId);

  const fetchedListingExtras =
    await userDashboardApiIsomorphicFetchListingExtras({
      request,
      context,
    })([orgId]);

  const saved = Boolean(fetchedListingExtras[org.id]?.saved);

  switch (org?.type) {
    case undefined:
      return {
        type: 'NOT_FOUND',
      } as const;
    case 'ORG':
      return {
        type: 'ORG',
        org,
        saved,
        handle: {
          hideFooterDivider: 'hide-footer-divider-if-logged-out',
          searchFromPageType: 'LISTING',
          urlByLocale: org.url,
        } satisfies LayoutHandleProps,
      } as const;
    case 'UNPUBLISHED_ORG':
      return {
        type: 'UNPUBLISHED_ORG',
        unpublishedOrg: org,
        handle: {
          hideFooterDivider: 'hide-footer-divider-if-logged-out',
        } satisfies LayoutHandleProps,
      } as const;
  }
}

export default function ViewHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  switch (loaderData.type) {
    case 'NOT_FOUND':
      return <NotFoundPageLoadable noLayout />;
    case 'ORG':
      if (loaderData.org.isFrozen) {
        return (
          <RouteModule
            title={`${loaderData.org.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['ORG']}`}
          >
            <UnpublishedOrgPageLoadable unpublishedOrg={loaderData.org} />
          </RouteModule>
        );
      }

      return (
        <RouteModule title={loaderData.org.name}>
          <ViewOrgPageLoadable
            initialOrg={loaderData.org}
            initiallySaved={loaderData.saved}
            isPublished
            isPreview={false}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_ORG':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <UnpublishedOrgPageLoadable
            unpublishedOrg={loaderData.unpublishedOrg}
          />
        </RouteModule>
      );
  }
}
